<template>
  <div v-if="plate">
    <h3>Pitch</h3>

    <v-checkbox
      :label="`${lead.firstname}, there are 13 different rate classes
      I want to make sure I have your health history correct:`"
      v-model="plate.isCorrectAsked"
      :error="$v.plate.isCorrectAsked.$error"
    />

    <!--  health history   -->

    <div class="d-flex justify-center">
      <div class="d-flex flex-column">
        <div
          v-for="(key, k) in selectedDisease"
          :key="k"
          class="d-inline-flex font-weight-medium"
        >
          {{ diseaseDetails(key) }}
        </div>
      </div>
    </div>

    <div class="mr-4">Do I have that correct?</div>

    <v-radio-group
      v-model="plate.isCorrect"
      class="d-flex radio-questions"
      column
    >
      <v-radio label="Yes" :value="true" />
      <v-radio label="No" :value="false" />
    </v-radio-group>

    <v-checkbox
      class="radio-buttons mr-4"
      label="Because you told me you life Insurance for:"
      v-model="plate.isTold"
      :error="$v.plate.isTold.$error"
    />

    <!--  selected needs   -->

    <div class="my-4 ml-8">
      <div class="d-flex mb-2 body-1" v-for="(type, k) in needsList" :key="k">
        <div class="mr-2">-</div>
        <div>{{ needDetails(type) }}</div>
      </div>
    </div>

    <v-checkbox
      :label="`The recommended coverage amount is $${
        lead.coverage_amount
      } is ONLY
      ${
        isLoading ? '...' : `$${plate.monthlyPremium}`
      }, I assume that's more than affordable, or we can increase by $1000.`"
      v-model="plate.isCoverage"
      :error="$v.plate.isCoverage.$error"
    />

    <div class="d-flex justify-center mt-4">
      <v-btn color="primary">
        Apply for this product
        <v-icon right dark> mdi-chevron-right</v-icon>
      </v-btn>
    </div>

    <v-row class="my-4">
      <v-col>
        <v-select
          :items="baseData.terms"
          item-text="name"
          item-value="code"
          menu-props="auto"
          label="Product Type"
          v-model="lead.term"
          @change="loadPrimeSelect"
        />
      </v-col>

      <v-col>
        <v-select
          v-if="lead.term === 'fex'"
          :items="baseData.fexRateClass"
          itemText="name"
          itemValue="code"
          label="Rate Class"
          menu-props="auto"
          v-model="lead.rate_class"
          @change="loadPrime"
        />
        <v-select
          v-else
          :items="baseData.rateClass"
          itemText="name"
          itemValue="code"
          label="Rate Class"
          menu-props="auto"
          v-model="lead.rate_class"
          @change="loadPrime"
        />
      </v-col>

      <v-col>
        <v-select
          label="Company"
          :items="baseData.quoter_companies"
          itemText="body"
          itemValue="code"
          menu-props="auto"
          v-model="plate.company"
          @change="loadPrime"
        />
      </v-col>

      <v-col>
        <v-switch
          inset
          label="Nicotine use"
          v-model="lead.tobacco"
          @change="loadPrime"
        />
      </v-col>
    </v-row>

    <h4 class="mt-6">{{ lead.firstname }} Recommended Coverage Range</h4>

    <div class="coverage-buttons">
      <div class="coverage-buttons__toggle">
        <v-btn-toggle class="my-2">
          <v-btn @click="decreaseCoverageAmount">
            ${{ +lead.coverage_amount - 1000 }}
          </v-btn>
          <v-btn>${{ lead.coverage_amount }}</v-btn>
          <v-btn @click="increaseCoverageAmount">
            ${{ +lead.coverage_amount + 1000 }}
          </v-btn>
        </v-btn-toggle>
      </div>

      <v-text-field
        v-model="lead.coverage_amount"
        label="Exact coverage amount"
        class="coverage-buttons__input"
        prefix="$"
        type="number"
        @input="onInputChange"
        :disabled="isLoading"
      />

      <div>
        ONLY
        <span v-if="isLoading">...</span>
        <span v-else class="font-weight-bold">${{ plate.monthlyPremium }}</span>
      </div>

      <v-btn color="primary">
        Apply for this product
        <v-icon right dark> mdi-chevron-right</v-icon>
      </v-btn>
    </div>

    <div class="d-flex justify-center mt-6" style="gap: 15px">
      <v-btn @click="isRebuttal = !isRebuttal" color="success">
        Rebuttal
      </v-btn>

      <v-btn @click="isCompareQuotes = !isCompareQuotes" color="warning">
        Compare Quotes
      </v-btn>
    </div>

    <QuotesCompare v-if="isCompareQuotes" :baseData="baseData" :lead="lead" />

    <QuotesRebuttal v-if="isRebuttal" :lead="lead" :beneficiary="beneficiary" />

    <PlateFooter
      :onBack="onBack"
      :onSubmit="onSubmit"
      :isLoading="isLoading"
      :errors="$v.$error"
    />
  </div>
</template>

<script>
import { format } from 'date-fns';
import { PlatesSteps, ProtectionTypes } from '@/common/variables';
import PlateFooter from '@/components/lead/plates/PlateFooter';
import { mapGetters } from 'vuex';
import QuotesCompare from '@/components/lead/plates/QuotesCompare';
import QuotesRebuttal from '@/components/lead/plates/QuotesRebuttal';
import { diseaseTextByType, isChecked } from '@/common/helpers';

export default {
  name: 'ResultPlate',
  components: { QuotesRebuttal, QuotesCompare, PlateFooter },
  computed: {
    ...mapGetters({
      disease: 'plates/disease',
      selectedDisease: 'plates/selectedDisease',
      baseData: 'baseData/baseData',
      needs: 'plates/needs',
      needsList: 'plates/needsList',
      defaultCompany: 'plates/appQuestion',
      defaultRate: 'plates/appRate',
    }),
  },
  props: {
    lead: Object,
    beneficiary: Object,
  },
  data() {
    return {
      plate: null,
      isLoading: false,
      timer: null,
      isRebuttal: false,
      isCompareQuotes: false,
    };
  },
  mounted() {
    const { data } = this.$store.getters['plates/dataByStep'](
      PlatesSteps.result
    );
    this.plate = data;
    this.plate.company = this.defaultCompany;

    if (this.defaultRate) {
      this.lead.rate_class = this.defaultRate;
    }

    this.loadPrime();
  },
  methods: {
    async loadPrime() {
      this.isLoading = true;

      try {
        const { data } = await this.$axios.post(`/lead/get-quote`, {
          birthdate: this.lead.birth_date,
          state: this.lead.state,
          gender: this.lead.gender,
          smoker: !!this.lead.tobacco,
          coverage: this.lead.coverage_amount,
          term: this.lead.term,
          rate_class: this.lead.rate_class,
          company: this.plate.company ?? this.baseData.quoter_companies[0].code,
        });

        this.plate = {
          ...this.plate,
          monthlyPremium: data?.premium_monthly || '-',
        };
      } catch (error) {
        console.log(error);
      } finally {
        this.isLoading = false;
      }
    },
    async loadPrimeSelect(val) {
      const isWrongRate = this.baseData.fexRateClass.find(
        (i) => i.code === this.lead.rate_class
      );

      if (val === 'fex') {
        this.lead.rate_class = this.baseData.fexRateClass[0].code;
      }

      if (isWrongRate) {
        this.lead.rate_class = this.baseData.rateClass[0].code;
      }

      await this.loadPrime();
    },
    async onInputChange() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.loadPrime();
      }, 600);
    },
    onBack() {
      this.$store.commit('plates/setStep', PlatesSteps.product);
    },
    onSubmit() {
      this.$v.$touch();
      if (this.$v.$invalid) return;

      this.$store.commit('plates/setStep', PlatesSteps.email);
      this.$store.dispatch('plates/updatePlate', this.lead);
    },
    increaseCoverageAmount() {
      this.lead.coverage_amount = +this.lead.coverage_amount + 1000;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.loadPrime();
      }, 600);
    },
    decreaseCoverageAmount() {
      this.lead.coverage_amount = +this.lead.coverage_amount - 1000;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.loadPrime();
      }, 600);
    },
    format,
    diseaseDetails(type) {
      return diseaseTextByType(type, this.disease[type]);
    },
    needDetails(type) {
      const protection = this.needs[type];

      switch (type) {
        case ProtectionTypes.debt:
          return `We DO need Debit protection, to avoid the reassigning
              of debits or collections. You have ${protection.amount} you'd like covered.`;

        case ProtectionTypes.mortgage:
          return `We do need mortgage protection, to make sure they'll have a
              roof over their head. You have ${protection.amount} left on the house
              with roughly ${protection.yearsLeft} years`;

        case ProtectionTypes.family: {
          const collegeText = `${protection.collegeExpenses ? 'Is' : 'Isn`t'}
           important to take care of college expenses`;

          const childrenText = `${
            protection.isAnyChildren
              ? `${protection.childrenCount} young ones, the youngest being
              ${protection.childrenAge} years old and it ${collegeText}`
              : ''
          }`;

          return `We ${protection.replacement ? 'Do' : 'Don`t'} need income
              replacement, to supplement the loss earning they would experience.
              You ${protection.isAnyChildren ? 'Do' : 'Don`t'} have children.
              ${childrenText}`;
        }

        case ProtectionTypes.finalExpenses: {
          const relationText =
            this.beneficiary.relationship === 'spouse'
              ? `I know ${this.beneficiary.name} is a ${this.beneficiary.status}. We
                want them to focus on your memory.`
              : `I know ${this.beneficiary.name} gave you ${
                  this.beneficiary.grandchildrenAmount
                }
                grandkids and ${
                  this.beneficiary.liveInSameState ? "won't" : 'will'
                }
                have to travel from out of town. We want them to focus on you and
                your memory.`;

          return `${this.lead.firstname}, you want to make sure ${this.beneficiary.name}
            doesn't have to come out of pocket for the ${protection.target}. ${relationText}`;
        }

        default:
          return '';
      }
    },
  },
  validations: {
    plate: {
      isCorrectAsked: { checked: isChecked },
      isTold: { checked: isChecked },
      isCoverage: { checked: isChecked },
    },
  },
};
</script>

<style lang="scss" scoped>
.radio-buttons {
  margin: 0;
}

.coverage-buttons {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 30px;

  &__toggle {
    .v-btn {
      min-width: 125px !important;
    }
  }

  &__input {
    flex: 0 0 150px;

    .v-text-field__details {
      display: none !important;
    }
  }
}
</style>